// types/index.ts
// hooks/useAudioEdit.ts
import { useState, useCallback, useRef } from "react";
import { convert32to16, writeString } from "../../module/audio/audio.util";
import * as lamejs from "@breezystack/lamejs";

export interface AudioSelection {
  start: number | null;
  end: number | null;
}

interface AudioEditHistory {
  buffer: AudioBuffer;
  originalBuffer: AudioBuffer | null;
  selection: AudioSelection;
  timestamp: number;
  action: "initial" | "mute" | "delete" | "other";
}
export type AudioFormat = "audio/wav" | "audio/mp3";

export interface HistoryState {
  canUndo: boolean;
  canRedo: boolean;
}

// utils/audioUtils.ts
export class AudioEditUtils {
  static async createNewAudioBuffer(
    originalBuffer: AudioBuffer,
    startTime: number,
    endTime: number,
    audioContext: AudioContext
  ): Promise<AudioBuffer> {
    const sampleRate = originalBuffer.sampleRate;
    const startOffset = Math.floor(startTime * sampleRate);
    const endOffset = Math.floor(endTime * sampleRate);
    const newLength = endOffset - startOffset;

    const newBuffer = audioContext.createBuffer(
      originalBuffer.numberOfChannels,
      newLength,
      sampleRate
    );

    for (
      let channel = 0;
      channel < originalBuffer.numberOfChannels;
      channel++
    ) {
      const channelData = originalBuffer.getChannelData(channel);
      const newChannelData = newBuffer.getChannelData(channel);
      for (let i = 0; i < newLength; i++) {
        newChannelData[i] = channelData[i + startOffset];
      }
    }

    return newBuffer;
  }

  static async concatAudioBuffers(
    buffer1: AudioBuffer,
    buffer2: AudioBuffer,
    audioContext: AudioContext
  ): Promise<AudioBuffer> {
    const totalLength = buffer1.length + buffer2.length;
    const result = audioContext.createBuffer(
      buffer1.numberOfChannels,
      totalLength,
      buffer1.sampleRate
    );

    for (let channel = 0; channel < buffer1.numberOfChannels; channel++) {
      const resultData = result.getChannelData(channel);
      resultData.set(buffer1.getChannelData(channel), 0);
      resultData.set(buffer2.getChannelData(channel), buffer1.length);
    }

    return result;
  }

  static async deleteSection(
    buffer: AudioBuffer,
    startTime: number,
    endTime: number,
    audioContext: AudioContext
  ): Promise<AudioBuffer> {
    const beforeSection = await this.createNewAudioBuffer(
      buffer,
      0,
      startTime,
      audioContext
    );

    const afterSection = await this.createNewAudioBuffer(
      buffer,
      endTime,
      buffer.duration,
      audioContext
    );

    return this.concatAudioBuffers(beforeSection, afterSection, audioContext);
  }

  static async copySection(
    buffer: AudioBuffer,
    startTime: number,
    endTime: number,
    audioContext: AudioContext
  ): Promise<AudioBuffer> {
    return this.createNewAudioBuffer(buffer, startTime, endTime, audioContext);
  }

  static async insertBuffer(
    originalBuffer: AudioBuffer,
    insertBuffer: AudioBuffer,
    position: number,
    audioContext: AudioContext
  ): Promise<AudioBuffer> {
    const beforeSection = await this.createNewAudioBuffer(
      originalBuffer,
      0,
      position,
      audioContext
    );

    const afterSection = await this.createNewAudioBuffer(
      originalBuffer,
      position,
      originalBuffer.duration,
      audioContext
    );

    const firstConcat = await this.concatAudioBuffers(
      beforeSection,
      insertBuffer,
      audioContext
    );

    return this.concatAudioBuffers(firstConcat, afterSection, audioContext);
  }

  static async normalizeAudio(
    buffer: AudioBuffer,
    targetPeak: number = 0.9
  ): Promise<AudioBuffer> {
    let maxPeak = 0;

    // Find the maximum peak
    for (let channel = 0; channel < buffer.numberOfChannels; channel++) {
      const channelData = buffer.getChannelData(channel);
      for (let i = 0; i < channelData.length; i++) {
        const absolute = Math.abs(channelData[i]);
        if (absolute > maxPeak) {
          maxPeak = absolute;
        }
      }
    }

    // Apply gain
    const gainFactor = targetPeak / maxPeak;

    for (let channel = 0; channel < buffer.numberOfChannels; channel++) {
      const channelData = buffer.getChannelData(channel);
      for (let i = 0; i < channelData.length; i++) {
        channelData[i] = channelData[i] * gainFactor;
      }
    }

    return buffer;
  }

  static async muteSection(
    buffer: AudioBuffer,
    startTime: number,
    endTime: number,
    audioContext: AudioContext
  ): Promise<AudioBuffer> {
    const newBuffer = audioContext.createBuffer(
      buffer.numberOfChannels,
      buffer.length,
      buffer.sampleRate
    );

    for (let channel = 0; channel < buffer.numberOfChannels; channel++) {
      const channelData = buffer.getChannelData(channel);
      const newChannelData = newBuffer.getChannelData(channel);

      // Copy the data before the muted section
      const startOffset = Math.floor(startTime * buffer.sampleRate);
      for (let i = 0; i < startOffset; i++) {
        newChannelData[i] = channelData[i];
      }

      // Set the muted section to zero
      const endOffset = Math.floor(endTime * buffer.sampleRate);
      for (let i = startOffset; i < endOffset; i++) {
        newChannelData[i] = 0;
      }

      // Copy the data after the muted section
      for (let i = endOffset; i < buffer.length; i++) {
        newChannelData[i] = channelData[i];
      }
    }

    return newBuffer;
  }

  static async audioBufferToWav(audioBuffer: AudioBuffer): Promise<Blob> {
    const numberOfChannels = audioBuffer.numberOfChannels;
    const length = audioBuffer.length * numberOfChannels * 2;
    const buffer = new ArrayBuffer(44 + length);
    const view = new DataView(buffer);

    // Write WAV header
    // "RIFF" identifier
    writeString(view, 0, "RIFF");
    // File length minus RIFF identifier length and file description length
    view.setUint32(4, 36 + length, true);
    // RIFF type "WAVE"
    writeString(view, 8, "WAVE");
    // Format chunk identifier "fmt "
    writeString(view, 12, "fmt ");
    // Format chunk length
    view.setUint32(16, 16, true);
    // Sample format (raw)
    view.setUint16(20, 1, true);
    // Channel count
    view.setUint16(22, numberOfChannels, true);
    // Sample rate
    view.setUint32(24, audioBuffer.sampleRate, true);
    // Byte rate (sample rate * block align)
    view.setUint32(28, audioBuffer.sampleRate * numberOfChannels * 2, true);
    // Block align (channel count * bytes per sample)
    view.setUint16(32, numberOfChannels * 2, true);
    // Bits per sample
    view.setUint16(34, 16, true);
    // Data chunk identifier "data"
    writeString(view, 36, "data");
    // Data chunk length
    view.setUint32(40, length, true);

    // Write audio data
    const offset = 44;
    for (let i = 0; i < audioBuffer.numberOfChannels; i++) {
      const channelData = audioBuffer.getChannelData(i);
      for (let j = 0; j < channelData.length; j++) {
        const index = offset + (j * numberOfChannels + i) * 2;
        const sample = Math.max(-1, Math.min(1, channelData[j]));
        view.setInt16(
          index,
          sample < 0 ? sample * 0x8000 : sample * 0x7fff,
          true
        );
      }
    }

    return new Blob([buffer], { type: "audio/wav" });
  }

  static async audioBufferToMP3(audioBuffer: AudioBuffer): Promise<Blob> {
    const mp3encoder = new lamejs.Mp3Encoder(
      audioBuffer.numberOfChannels,
      audioBuffer.sampleRate,
      320 // 높은 품질의 비트레이트
    );

    const mp3Data: Uint8Array[] = [];
    const sampleBlockSize = 1152; // MPEG-1 Layer 3 프레임당 샘플 수
    const channels = audioBuffer.numberOfChannels;

    try {
      if (channels === 2) {
        // 스테레오 처리
        const leftData = audioBuffer.getChannelData(0);
        const rightData = audioBuffer.getChannelData(1);
        const leftChunk = new Int16Array(sampleBlockSize);
        const rightChunk = new Int16Array(sampleBlockSize);

        for (let i = 0; i < audioBuffer.length; i += sampleBlockSize) {
          const currentBlockSize = Math.min(
            sampleBlockSize,
            audioBuffer.length - i
          );

          // 각 채널의 데이터를 개별적으로 처리
          for (let j = 0; j < currentBlockSize; j++) {
            leftChunk[j] = convert32to16(leftData[i + j]);
            rightChunk[j] = convert32to16(rightData[i + j]);
          }

          // 남은 공간을 0으로 채움
          if (currentBlockSize < sampleBlockSize) {
            leftChunk.fill(0, currentBlockSize);
            rightChunk.fill(0, currentBlockSize);
          }

          const mp3buf = mp3encoder.encodeBuffer(leftChunk, rightChunk);
          if (mp3buf.length > 0) {
            mp3Data.push(new Uint8Array(mp3buf));
          }
        }
      } else {
        // 모노 처리
        const channelData = audioBuffer.getChannelData(0);
        const chunk = new Int16Array(sampleBlockSize);

        for (let i = 0; i < audioBuffer.length; i += sampleBlockSize) {
          const currentBlockSize = Math.min(
            sampleBlockSize,
            audioBuffer.length - i
          );

          for (let j = 0; j < currentBlockSize; j++) {
            chunk[j] = convert32to16(channelData[i + j]);
          }

          if (currentBlockSize < sampleBlockSize) {
            chunk.fill(0, currentBlockSize);
          }

          const mp3buf = mp3encoder.encodeBuffer(chunk);
          if (mp3buf.length > 0) {
            mp3Data.push(new Uint8Array(mp3buf));
          }
        }
      }

      // 인코딩 완료 처리
      const end = mp3encoder.flush();
      if (end.length > 0) {
        mp3Data.push(new Uint8Array(end));
      }

      // 모든 청크 결합
      const totalLength = mp3Data.reduce((acc, buf) => acc + buf.length, 0);
      const result = new Uint8Array(totalLength);
      let offset = 0;
      for (const buf of mp3Data) {
        result.set(buf, offset);
        offset += buf.length;
      }

      return new Blob([result], { type: "audio/mp3" });
    } catch (error) {
      console.error("MP3 인코딩 오류:", error);
      throw new Error("MP3 인코딩 중 오류가 발생했습니다");
    }
  }
}

export const useAudioEdit = (audioContext: AudioContext) => {
  const historyRef = useRef<AudioEditHistory[]>([]);
  const currentIndexRef = useRef<number>(-1);
  const hasInitialStateRef = useRef<boolean>(false);

  // AudioBuffer 깊은 복사 유틸리티
  const cloneAudioBuffer = useCallback(
    (buffer: AudioBuffer): AudioBuffer => {
      const newBuffer = audioContext.createBuffer(
        buffer.numberOfChannels,
        buffer.length,
        buffer.sampleRate
      );

      for (let channel = 0; channel < buffer.numberOfChannels; channel++) {
        const newChannelData = newBuffer.getChannelData(channel);
        const originalChannelData = buffer.getChannelData(channel);
        newChannelData.set(originalChannelData);
      }

      return newBuffer;
    },
    [audioContext]
  );

  // 초기 상태 설정
  const initializeHistory = useCallback(
    (buffer: AudioBuffer) => {
      if (!hasInitialStateRef.current) {
        historyRef.current = [
          {
            buffer: cloneAudioBuffer(buffer),
            originalBuffer: null,
            selection: { start: null, end: null },
            timestamp: Date.now(),
            action: "initial",
          },
        ];
        currentIndexRef.current = 0;
        hasInitialStateRef.current = true;
      }
    },
    [cloneAudioBuffer]
  );

  // 히스토리 추가 함수 수정
  const addToHistory = useCallback(
    (
      buffer: AudioBuffer,
      selection: AudioSelection,
      action: "mute" | "delete" | "other" = "other",
      originalBuffer: AudioBuffer | null = null
    ) => {
      // 초기 상태가 없으면 초기화
      if (!hasInitialStateRef.current) {
        initializeHistory(buffer);
        return;
      }

      // 현재 위치 이후의 히스토리 제거
      historyRef.current = historyRef.current.slice(
        0,
        currentIndexRef.current + 1
      );

      // 새로운 상태 추가
      historyRef.current.push({
        buffer: cloneAudioBuffer(buffer),
        originalBuffer: originalBuffer
          ? cloneAudioBuffer(originalBuffer)
          : null,
        selection,
        timestamp: Date.now(),
        action,
      });

      currentIndexRef.current = historyRef.current.length - 1;
    },
    [cloneAudioBuffer, initializeHistory]
  );

  // Undo 함수 수정
  const undo = useCallback(() => {
    if (currentIndexRef.current > 0) {
      const previousState = historyRef.current[currentIndexRef.current - 1];
      currentIndexRef.current -= 1;

      return {
        ...previousState,
        buffer: cloneAudioBuffer(previousState.buffer),
      };
    }
    return null;
  }, [cloneAudioBuffer]);

  const redo = useCallback(() => {
    if (currentIndexRef.current < historyRef.current.length - 1) {
      const nextState = historyRef.current[currentIndexRef.current + 1];
      currentIndexRef.current += 1;

      return {
        ...nextState,
        buffer: cloneAudioBuffer(nextState.buffer),
      };
    }
    return null;
  }, [cloneAudioBuffer]);

  // 히스토리 상태 확인 함수 수정
  const getHistoryState = useCallback((): HistoryState => {
    return {
      canUndo: currentIndexRef.current > 0,
      canRedo: currentIndexRef.current < historyRef.current.length - 1,
    };
  }, []);

  const cutSelection = useCallback(
    async (buffer: AudioBuffer, selection: AudioSelection) => {
      if (!selection.start || !selection.end) return buffer;

      const copiedSection = await AudioEditUtils.copySection(
        buffer,
        selection.start,
        selection.end,
        audioContext
      );

      const newBuffer = await AudioEditUtils.deleteSection(
        buffer,
        selection.start,
        selection.end,
        audioContext
      );

      addToHistory(newBuffer, selection);
      return newBuffer;
    },
    [audioContext, addToHistory]
  );

  const exportAudioBuffer = async (
    audioBuffer: AudioBuffer,
    format: AudioFormat
  ): Promise<Blob> => {
    switch (format) {
      case "audio/wav":
        return AudioEditUtils.audioBufferToWav(audioBuffer);
      case "audio/mp3":
        return AudioEditUtils.audioBufferToMP3(audioBuffer);
      default:
        throw new Error(`Unsupported format: ${format}`);
    }
  };

  return {
    initializeHistory, // 새로 export
    addToHistory,
    getHistoryState,
    undo,
    redo,
    cutSelection,
    exportAudioBuffer,
  };
};
